import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title?: ReactNode;
  description?: ReactNode;
  /** Header-right actions. */
  actions?: ReactNode;
  footer?: ReactNode;
  /** Remove body padding (e.g. for a table edge to edge). */
  flush?: boolean;
  /** Heading level of the title: 2 for a section under the page title, 3 inside another section. */
  level?: 2 | 3 | 4;
  children?: ReactNode;
}

export function Card({ title, description, actions, footer, flush, level = 2, className, children, ...rest }: CardProps) {
  const Heading = `h${level}` as "h2" | "h3" | "h4";
  return (
    <section {...rest} className={cx("pmu-card", className)}>
      {(title || actions) && (
        <header className="pmu-card__header">
          <div>
            {title && <Heading className="pmu-card__title">{title}</Heading>}
            {description && <p className="pmu-card__description">{description}</p>}
          </div>
          {actions && <div className="pmu-card__actions">{actions}</div>}
        </header>
      )}
      {children && <div className={cx("pmu-card__body", flush && "pmu-card__body--flush")}>{children}</div>}
      {footer && <footer className="pmu-card__footer">{footer}</footer>}
    </section>
  );
}

export interface EmptyStateProps {
  icon?: IconName;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon = "list", title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cx("pmu-empty", className)}>
      <span className="pmu-empty__icon"><Icon name={icon} size="100%" /></span>
      <p className="pmu-empty__title">{title}</p>
      {description && <p className="pmu-empty__description">{description}</p>}
      {action && <div className="pmu-empty__action">{action}</div>}
    </div>
  );
}
